import React, { useState } from 'react';
import {
  alpha,
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Slide,
  Typography,
  UIButton as Button,
  useMediaQuery,
  useTheme,
} from '../ui';
import CheckBoxOutlineBlankRoundedIcon from '@mui/icons-material/CheckBoxOutlineBlankRounded';
import CheckBoxRoundedIcon from '@mui/icons-material/CheckBoxRounded';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';

interface ListFormItem {
  [key: string]: any;
}

// Removed unused imports: FormProvider and useForm from react-hook-form

interface FieldConfig {
  fieldName: string;
  dataType: string;
  label: string | null;
  [key: string]: any;
}

interface ListFormProps {
  label?: string | null;
  showAsCheckBox?: boolean;
  addMore?: boolean;
  formTitle?: string;
  formFields?: FieldConfig[];
  value?: ListFormItem[];
  onChange?: (items: ListFormItem[]) => void;
  error?: boolean;
  helperText?: string | undefined;
  renderField?: (field: FieldConfig) => React.ReactNode;
  form?: any;
  fieldName?: string;
}

// Transition component for mobile slide-up
const Transition = React.forwardRef<unknown, { children: React.ReactElement }>(
  function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
  }
);

const ListForm: React.FC<ListFormProps> = ({
  label,
  showAsCheckBox = false,
  addMore = true,
  formTitle = 'Add Item',
  formFields = [],
  value = [],
  onChange,
  error = false,
  helperText,
  renderField,
  form,
  fieldName,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [isChecked, setIsChecked] = useState(value.length > 0);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const wasSavedRef = React.useRef(false);

  // Use a unique prefix for temporary form fields to avoid conflicts with parent form
  const tempFieldPrefix = `__listform_temp_${fieldName || 'default'}__`;

  // No arrears-specific behavior in ListForm: keep this component generic.

  // Handle checkbox toggle
  const handleCheckboxToggle = () => {
    const newChecked = !isChecked;
    setIsChecked(newChecked);
    
    // If unchecking, clear all items
    if (!newChecked && onChange) {
      onChange([]);
    }
  };

  // Handle add button click
  const handleAddClick = () => {
    setEditingIndex(null);
    // Clear temporary form fields in parent form
    if (form && formFields) {
      formFields.forEach((field: FieldConfig) => {
        form.setValue(`${tempFieldPrefix}.${field.fieldName}`, undefined);
      });
    }
    setIsDialogOpen(true);
  };

  // Handle edit button click
  const handleEditClick = (index: number) => {
    setEditingIndex(index);
    const itemToEdit = value[index];
    // Pre-populate temporary form fields in parent form
    if (form && formFields && itemToEdit) {
      formFields.forEach((field: FieldConfig) => {
        const fieldValue = itemToEdit[field.fieldName];
        form.setValue(`${tempFieldPrefix}.${field.fieldName}`, fieldValue);
      });
    }
    setIsDialogOpen(true);
  };

  // Handle delete button click
  const handleDeleteClick = (index: number) => {
    if (onChange) {
      const newItems = value.filter((_, i) => i !== index);
      onChange(newItems);
      
      // If no items left and checkbox is shown, uncheck it
      if (newItems.length === 0 && showAsCheckBox) {
        setIsChecked(false);
      }
    }
  };

  // Handle dialog close
  const handleDialogClose = () => {
    setIsDialogOpen(false);
    setEditingIndex(null);
    // Clear temporary form fields in parent form
    if (form && formFields) {
      formFields.forEach((field: FieldConfig) => {
        form.setValue(`${tempFieldPrefix}.${field.fieldName}`, undefined);
      });
    }

    // If dialog was closed without saving and there are no items, revert arrears toggle to No
    try {
      if (!wasSavedRef.current && Array.isArray(value) && value.length === 0 && form && fieldName) {
        const fieldPath = String(fieldName || '');
        if (fieldPath.includes('.')) {
          const parts = fieldPath.split('.');
          parts[parts.length - 1] = 'inArrears';
          const inArrearsPath = parts.join('.');
          // Set the arrears toggle to false
          form.setValue(inArrearsPath, false, { shouldValidate: true });
        }
      }
    } catch {
      // no-op if we can't reset the toggle
    } finally {
      wasSavedRef.current = false;
    }
  };

  // Handle form save
  const handleSave = () => {
    wasSavedRef.current = true;
    // Collect data from temporary form fields
    const collectedData: ListFormItem = {};
    if (form && formFields) {
      formFields.forEach((field: FieldConfig) => {
        const fieldValue = form.getValues(`${tempFieldPrefix}.${field.fieldName}`);
        if (fieldValue !== undefined) {
          collectedData[field.fieldName] = fieldValue;
        }
      });
    }
    
    console.log('Saving collected data:', collectedData);
    
    if (onChange) {
      let newItems: ListFormItem[];
      
      if (editingIndex !== null) {
        // Update existing item
        newItems = value.map((item, i) => (i === editingIndex ? collectedData : item));
      } else {
        // Add new item
        newItems = [...value, collectedData];
      }
      
      onChange(newItems);
    }
    
    handleDialogClose();
  };

  // Handle cancel
  const handleCancel = () => {
    handleDialogClose();
  };

  // Show add button condition
  const hasCheckbox = Boolean(showAsCheckBox && label);
  const shouldShowAddButton = hasCheckbox ? isChecked : true;
  const canAddMore = addMore || value.length === 0;

  // Get display text for list item
  const getItemDisplayText = (item: ListFormItem) => {
    // Log the item to see what data we're getting
    console.log('ListForm item data:', item);

    // Handle both the value and label from select fields
    const companyName = item.companyName || item.company || 'Unknown Company';
    const reference = item.referenceNumber || item.reference || '';

    // Handle both direct amountCurrentlyOwned and nested amountCurrentlyOwned.amount structure
    let amount = '';
    if (typeof item.amountCurrentlyOwned === 'object' && item.amountCurrentlyOwned?.amount) {
      amount = item.amountCurrentlyOwned.amount;
    } else {
      amount = item.amountCurrentlyOwned || item.amount || '';
    }

    // Build subtitle parts
    const subtitleParts = [];
    if (reference) subtitleParts.push(`REF: ${reference}`);
    if (amount) subtitleParts.push(`Amount owed : £${amount}`);

    return {
      title: companyName,
      subtitle: subtitleParts.join(' | '),
    };
  };

  return (
    <Box data-testid="components-list-form-container-action-1" sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {/* Checkbox (if showAsCheckBox is true and label provided) */}
      {hasCheckbox && (
        <Box data-testid="components-list-form-container-handle-checkbox-toggle"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            cursor: 'pointer',
            mb: 2,
          }}
          onClick={handleCheckboxToggle}
        >
          {isChecked ? (
            <CheckBoxRoundedIcon
              sx={{
                fontSize: '20px',
                marginRight: 1,
                color: '#2e7d32',
              }}
            />
          ) : (
            <CheckBoxOutlineBlankRoundedIcon
              sx={{
                fontSize: '20px',
                marginRight: 1,
                color: 'grey.500',
              }}
            />
          )}
          <Typography data-testid="components-list-form-text-action-1"
            variant="body2"
            sx={{
              color: 'text.primary',
              fontSize: '14px',
              userSelect: 'none',
            }}
          >
            {label}
          </Typography>
        </Box>
      )}

      {/* Add Button */}
      {shouldShowAddButton && canAddMore && (
        <Button data-testid="components-list-form-btn-handle-add-click"
          variant="contained"
          onClick={handleAddClick}
          sx={{
            backgroundColor: '#003d5c',
            color: 'white',
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '14px',
            padding: '10px 24px',
            borderRadius: '20px',
            mb: value.length > 0 ? 2 : 0,
            '&:hover': {
              backgroundColor: '#002940',
            },
          }}
        >
          {formTitle}
        </Button>
      )}

      {/* List Items */}
      {value.length > 0 && (
        <Box data-testid="components-list-form-container-action-2" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {value.map((item, index) => {
            const displayText = getItemDisplayText(item);
            return (
              <Paper data-testid="components-list-form-container-action-3"
                key={index}
                elevation={0}
                sx={{
                  p: 2,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: '4px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  backgroundColor: 'white',
                }}
              >
                <Box data-testid="components-list-form-container-action-4" sx={{ flex: 1 }}>
                  <Typography data-testid="components-list-form-text-action-2"
                    variant="body1"
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.text.primary,
                      fontSize: '14px',
                      mb: 0.5,
                    }}
                  >
                    {displayText.title}
                  </Typography>
                  {displayText.subtitle && (
                    <Typography data-testid="components-list-form-text-action-3"
                      variant="body2"
                      sx={{
                        color: theme.palette.text.secondary,
                        fontSize: '12px',
                      }}
                    >
                      {displayText.subtitle}
                    </Typography>
                  )}
                </Box>
                <Box data-testid="components-list-form-container-action-5" sx={{ display: 'flex', gap: 0.5 }}>
                  <IconButton data-testid="components-list-form-btn-action-1"
                    size="small"
                    onClick={() => handleEditClick(index)}
                    sx={{
                      color: '#757575',
                      padding: '6px',
                    }}
                  >
                    <EditIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                  <IconButton data-testid="components-list-form-btn-action-2"
                    size="small"
                    onClick={() => handleDeleteClick(index)}
                    sx={{
                      color: '#e53935',
                      padding: '6px',
                    }}
                  >
                    <DeleteIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Box>
              </Paper>
            );
          })}
        </Box>
      )}

      {/* Error message - matching TextField.tsx error handling pattern */}
      {(error || helperText) && (
        <Typography data-testid="components-list-form-text-action-4"
          variant="body2"
          color="error"
          sx={{
            mt: 1,
            fontSize: '0.875rem',
            fontWeight: 500,
            display: 'block',
          }}
        >
          {helperText || 'This field is required'}
        </Typography>
      )}

      {/* Dialog/Bottom Sheet */}
      <Dialog data-testid="components-list-form-modal-action"
        open={isDialogOpen}
        onClose={handleDialogClose}
        fullScreen={isMobile}
        {...(isMobile && { TransitionComponent: Transition })}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : '12px',
            ...(isMobile && {
              margin: 0,
              maxHeight: '90vh',
              position: 'fixed',
              bottom: 0,
              left: 0,
              right: 0,
              borderRadius: '16px 16px 0 0',
            }),
          },
        }}
      >
        <DialogTitle data-testid="components-list-form-header-action"
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pb: 2,
            pt: 2.5,
            px: 3,
          }}
        >
          <Typography data-testid="components-list-form-text-action-5"
            variant="h6"
            sx={{
              fontWeight: 600,
              fontSize: '20px',
              color: theme.palette.text.primary,
            }}
          >
            {formTitle}
          </Typography>
          <IconButton data-testid="components-list-form-btn-handle-dialog-close"
            onClick={handleDialogClose}
            size="small"
            sx={{
              color: theme.palette.text.secondary,
              '&:hover': {
                backgroundColor: alpha(theme.palette.text.secondary, 0.1),
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            px: 3,
            pb: 3,
            pt: 1,
          }}
        >
          <Box data-testid="components-list-form-container-action-6"
            component="form"
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleSave();
            }}
          >
            {/* Render form fields */}
            <Box data-testid="components-list-form-container-action-7" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {formFields.map((field: FieldConfig) => {
                if (renderField) {
                  // Prefix the field name to create a temporary scoped field
                  const scopedField = {
                    ...field,
                    fieldName: `${tempFieldPrefix}.${field.fieldName}`,
                  };
                  return (
                    <Box data-testid="components-list-form-container-action-8" key={field.fieldName}>
                      {renderField(scopedField)}
                    </Box>
                  );
                }
                return null;
              })}
            </Box>

            {/* Action Buttons */}
            <Box data-testid="components-list-form-container-action-9"
              sx={{
                display: 'flex',
                gap: 2,
                mt: 3,
                justifyContent: 'flex-end',
              }}
            >
              <Button data-testid="components-list-form-btn-handle-cancel"
                type="button"
                variant="text"
                onClick={handleCancel}
                sx={{
                  color: theme.palette.error.main,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '16px',
                  padding: '10px 32px',
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.error.main, 0.1),
                  },
                }}
              >
                Cancel
              </Button>
              <Button data-testid="components-list-form-btn-save"
                type="submit"
                variant="contained"
                sx={{
                  backgroundColor: theme.palette.success.main,
                  color: 'white',
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '16px',
                  padding: '10px 32px',
                  borderRadius: '8px',
                  '&:hover': {
                    backgroundColor: theme.palette.success.dark,
                  },
                }}
              >
                Save
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ListForm;
